import React, { useState, useRef, useCallback, SetStateAction } from 'react';
import { Modal } from 'antd';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { IAnexosDTO } from 'utils/DTOS';
import { FaFile, FaTrash } from 'react-icons/fa';
import Select from 'components/Select';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import { AxiosError, AxiosResponse } from 'axios';
import Input from '../Input';
import FileInput from '../Input/file';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';

// Styled components
import {
  ModalCSS,
  Title,
  AnexosList,
  Indice,
  Lista,
  Linha,
  AnexosCriar,
  FormHolder,
  FormButtonHolder,
  FormButton,
  Upload,
} from './styles';

interface IModalAnexos {
  projeto_id?: string;
  anexos: IAnexosDTO[];
  update: boolean;
  setUpdate: React.Dispatch<SetStateAction<boolean>>;
}

const ModalAnexos: React.FC<IModalAnexos> = props => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();

  const { projeto_id, anexos, update, setUpdate, children } = props;

  const showModal = useCallback(() => {
    setVisible(true);
  }, []);

  const handleCancel = useCallback(() => {
    setVisible(false);
  }, []);

  const handleSubmit = useCallback(
    async data => {
      if (!projeto_id) {
        addToast({
          type: 'error',
          title: 'Erro em enviar o anexo',
          description:
            'Ocorreu um erro ao enviar o anexo, recarregue a página.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();

      formData.append('file', data.file);
      formData.append('nome', data.nome);
      formData.append('cod_tipo', data.cod_tipo);
      formData.append('projeto_id', projeto_id);

      api
        .post(`/anexos`, formData)
        .then(() => {
          addToast({
            type: 'success',
            title: 'Anexo enviado com sucesso',
          });
          setUpdate(!update);
        })
        .catch(err => {
          addToast({
            type: 'error',
            title: 'Erro em enviar o anexo',
            description:
              'Ocorreu um erro ao enviar o anexo, verifique os dados do formulário.',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [addToast, projeto_id, update, setUpdate]
  );

  const getFile = useCallback(
    async (anexo_id, anexo_filename) => {
      addToast({
        type: 'success',
        title: 'Iniciando download do arquivo.',
      });

      await api
        .get(`/anexos/anexo-file/${anexo_id}`, {
          responseType: 'blob',
        })
        .then(async (res: AxiosResponse) => {
          const blob = new Blob([res.data], {
            type: 'text/plain;charset=utf-8',
          });

          const element = document.createElement('a');
          element.href = URL.createObjectURL(blob);
          element.download = anexo_filename;
          document.body.appendChild(element); // Required for this to work in FireFox
          element.click();

          addToast({
            type: 'success',
            title: 'Download finalizado com sucesso.',
          });
        })
        .catch((err: AxiosError) => {
          addToast({
            type: 'error',
            title:
              typeof err.response?.data.message === 'string'
                ? err.response?.data.message
                : 'Ocorreu um erro',
            description:
              'Ocorreu um erro ao fazer o download do arquivo, tente novamente.',
          });
          console.error(`Erro: ${err}`);
        });
    },
    [addToast]
  );

  return (
    <>
      <ModalCSS />
      <button
        type="button"
        onClick={e => {
          // To stop the page reloading
          e.preventDefault();
          // Lets track that custom click
          showModal();
        }}
      >
        {children}
      </button>
      <Modal
        visible={visible}
        footer={false}
        confirmLoading={false}
        onCancel={handleCancel}
        className="modal"
        wrapClassName="wrapForm"
      >
        <Title>ANEXOS</Title>
        {anexos && anexos.length > 0 && (
          <AnexosList>
            <Indice>
              <div className="nome">Nome</div>
              <div className="tipo">Tipo</div>
              <div className="visualizar">Ações</div>
            </Indice>

            {anexos.map(anexo => (
              <Lista>
                <Linha>
                  <div className="nome">{anexo.nome}</div>
                  <div className="tipo">{anexo.des_tipo}</div>
                  <div className="visualizar">
                    <FaFile
                      size={15}
                      onClick={() => {
                        getFile(anexo.id, anexo.filename);
                      }}
                    />
                    <ConfirmDeleteModal
                      delete_url="/anexos/anexo"
                      id={anexo.id.toString()}
                      module="anexo"
                      setUpdate={setUpdate}
                      update={update}
                    >
                      <FaTrash />
                    </ConfirmDeleteModal>
                  </div>
                </Linha>
              </Lista>
            ))}
          </AnexosList>
        )}
        <AnexosCriar>
          <strong>Adicionar anexo</strong>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <FormHolder>
              <label htmlFor="nome">Nome do arquivo</label>
              <Input id="nome" name="nome" placeholder="Nome" />
              <label htmlFor="tipo">Tipo do arquivo</label>
              <Select
                options={[
                  { value: 0, label: 'Técnico' },
                  { value: 1, label: 'Financeiro' },
                ]}
                name="cod_tipo"
                id="cod_tipo"
              />
              <FormButtonHolder>
                <FormButton type="submit">
                  {loading ? 'Carregando...' : 'ENVIAR'}
                </FormButton>
              </FormButtonHolder>
            </FormHolder>
            <Upload>
              <FileInput name="file" />
            </Upload>
          </Form>
        </AnexosCriar>
      </Modal>
    </>
  );
};

export default ModalAnexos;
